<template>
    <div class="space-y-4">
        <p class="text-sm text-slate-500">
            {{ $t('help_campaign_translations') }}
        </p>
        <p
            v-if="loading"
            class="text-sm text-slate-500"
        >
            {{ $t('loading') }}
        </p>
        <template v-else>
            <div
                v-for="language in otherLanguages"
                :key="language.value"
                class="rounded border border-slate-200 p-3"
            >
                <p class="mb-2 text-sm font-semibold">{{ language.label }}</p>

                <div
                    v-if="linkedByLanguage[language.value]"
                    class="flex items-center justify-between gap-3"
                >
                    <div class="min-w-0">
                        <p class="truncate text-sm">
                            {{ linkedByLanguage[language.value].label }}
                        </p>
                        <p class="truncate text-xs text-slate-500">
                            {{ linkedByLanguage[language.value].slug }}
                        </p>
                    </div>
                    <button
                        type="button"
                        class="btn-sm text-danger-500 shrink-0 border-slate-200 hover:border-slate-300"
                        :disabled="busy"
                        @click="unlink(linkedByLanguage[language.value])"
                    >
                        {{ $t('campaign_translation_unlink') }}
                    </button>
                </div>

                <p
                    v-else-if="!candidatesByLanguage[language.value]"
                    class="text-sm text-slate-500"
                >
                    {{ $t('campaign_translation_no_candidates') }}
                </p>

                <div
                    v-else
                    class="flex items-center justify-center gap-3"
                >
                    <div class="min-w-0 flex-1">
                        <SelectField
                            v-model="selection[language.value]"
                            :name="`translation_${language.value}`"
                            :options="candidatesByLanguage[language.value]"
                            :placeholder="$t('campaign_translation_select')"
                            :append-to-body="true"
                        />
                    </div>
                    <button
                        type="button"
                        class="btn-sm bg-primary-accent-500 shrink-0 text-white"
                        :disabled="busy || !selection[language.value]"
                        @click="link(language.value)"
                    >
                        {{ $t('campaign_translation_link') }}
                    </button>
                </div>
            </div>

            <div
                v-if="linked.length"
                class="rounded border border-slate-200 p-3"
            >
                <p class="mb-1 text-sm font-semibold">
                    {{ $t('campaign_x_default') }}
                </p>
                <p class="mb-2 text-xs text-slate-500">
                    {{ $t('help_campaign_x_default') }}
                </p>
                <SelectField
                    v-model="xDefaultSelection"
                    name="x_default"
                    :options="xDefaultOptions"
                    :required="true"
                    :append-to-body="true"
                    :disabled="busy"
                />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import SelectField from '@src/components/SelectField.vue'
import {
    Campaign,
    CampaignTranslation,
    CampaignTranslationsResponse,
} from '@src/types'
import {
    getCampaignTranslations,
    linkCampaignTranslation,
    setCampaignXDefault,
    unlinkCampaignTranslation,
} from '@src/utils/api/campaigns'
import { useToastStore } from '@stores/toast'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// The language versions of a campaign: which campaign of the same asset the website's language
// switcher and hreflang links point to per language. Linked by hand, because an asset can have
// several campaigns per language. Same layout as BlogPostTranslations.vue.
const props = defineProps<{
    campaign: Campaign
}>()

const i18n = useI18n()
const toast = useToastStore()

const LANGUAGES = [
    { label: 'German', value: 'de' },
    { label: 'English', value: 'en' },
    { label: 'Italian', value: 'it' },
]

// SelectField treats an empty value as nothing selected, so "not set" needs a value of its own.
const NO_X_DEFAULT = 'none'

const otherLanguages = computed(() =>
    LANGUAGES.filter((language) => language.value !== props.campaign.language)
)

const loading = ref(true)
const busy = ref(false)
const isXDefault = ref(false)
const linked = ref<CampaignTranslation[]>([])
const candidates = ref<CampaignTranslation[]>([])
const selection = reactive<Record<string, string | null>>({})

const campaignUid = computed(() => props.campaign.asset_var_uid as string)

const linkedByLanguage = computed<Record<string, CampaignTranslation>>(() =>
    Object.fromEntries(linked.value.map((item) => [item.language, item]))
)

// Labelled "<title> (<status>)" by the API, so campaigns with the same title can be told apart.
const candidatesByLanguage = computed(() => {
    const result: Record<string, { label: string; value: string }[]> = {}
    for (const candidate of candidates.value) {
        ;(result[candidate.language] ??= []).push({
            label: candidate.label,
            value: candidate.asset_var_uid,
        })
    }
    return result
})

// This campaign plus the linked ones, or none chosen.
const xDefaultOptions = computed(() => [
    { label: i18n.t('campaign_x_default_none'), value: NO_X_DEFAULT },
    {
        label: `${
            props.campaign.asset_title
        } (${props.campaign.language?.toUpperCase()})`,
        value: campaignUid.value,
    },
    ...linked.value.map((item) => ({
        label: `${item.label} (${item.language.toUpperCase()})`,
        value: item.asset_var_uid,
    })),
])

const xDefault = computed(() => {
    if (isXDefault.value) return campaignUid.value
    return (
        linked.value.find((item) => item.is_x_default)?.asset_var_uid ??
        NO_X_DEFAULT
    )
})

// SelectField clears its value to null while it is focused (for searching) and restores it on blur,
// so only a real choice that differs from the saved one is sent.
const xDefaultSelection = ref<string | null>(NO_X_DEFAULT)
watch(xDefault, (value) => (xDefaultSelection.value = value), {
    immediate: true,
})
watch(xDefaultSelection, (value) => {
    if (value === null || value === xDefault.value) return
    updateXDefault(value)
})

function apply(data: CampaignTranslationsResponse) {
    isXDefault.value = data.is_x_default
    linked.value = data.linked_translations
    candidates.value = data.candidates
}

async function load() {
    loading.value = true
    try {
        apply((await getCampaignTranslations(campaignUid.value)).data)
    } finally {
        loading.value = false
    }
}

async function link(language: string) {
    const translationUid = selection[language]
    if (!translationUid) return
    busy.value = true
    try {
        apply(
            (await linkCampaignTranslation(campaignUid.value, translationUid))
                .data
        )
        selection[language] = null
        toast.success(i18n.t('toast_success_campaign_translation_linked'))
    } finally {
        busy.value = false
    }
}

async function unlink(translation: CampaignTranslation) {
    busy.value = true
    try {
        apply(
            (
                await unlinkCampaignTranslation(
                    campaignUid.value,
                    translation.asset_var_uid
                )
            ).data
        )
        toast.success(i18n.t('toast_success_campaign_translation_unlinked'))
    } finally {
        busy.value = false
    }
}

async function updateXDefault(value: string) {
    busy.value = true
    try {
        apply(
            (
                await setCampaignXDefault(
                    campaignUid.value,
                    value === NO_X_DEFAULT ? null : value
                )
            ).data
        )
        toast.success(i18n.t('toast_success_campaign_x_default_updated'))
    } finally {
        // Back to the saved value (also when the request failed).
        xDefaultSelection.value = xDefault.value
        busy.value = false
    }
}

onMounted(load)
</script>
